/**
 * LogsStatsCards Component
 * Compact inline metrics for the Activity Logs header (uses space beside Refresh).
 */

import React from 'react';
import {
  Activity,
  AlertCircle,
  Users,
  Layers,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { LogsStats } from '../../types';

interface LogsStatsCardsProps {
  stats: LogsStats;
  isLoading?: boolean;
  className?: string;
}

export function LogsStatsCards({
  stats,
  isLoading,
  className,
}: LogsStatsCardsProps) {
  const errorRate =
    stats.total > 0
      ? Math.round((stats.errors / stats.total) * 1000) / 10
      : 0;

  const items = [
    {
      key: 'total',
      label: 'Actions',
      value: isLoading ? '—' : stats.total.toLocaleString(),
      icon: Activity,
      iconClass: 'bg-[#CCE561]/30 text-[#334238]',
    },
    {
      key: 'errors',
      label: 'Error rate',
      value: isLoading ? '—' : `${errorRate}%`,
      icon: AlertCircle,
      iconClass: 'bg-red-50 text-red-500',
    },
    {
      key: 'admins',
      label: 'Admins',
      value: isLoading ? '—' : stats.activeUsers.toLocaleString(),
      icon: Users,
      iconClass: 'bg-blue-50 text-blue-500',
    },
    {
      key: 'categories',
      label: 'Categories',
      value: isLoading ? '—' : stats.categories.toLocaleString(),
      icon: Layers,
      iconClass: 'bg-gray-100 text-gray-600',
    },
  ] as const;

  return (
    <div
      className={cn(
        'flex items-center gap-2 min-w-0 flex-1 justify-end overflow-x-auto',
        className
      )}
    >
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <div
            key={item.key}
            className="flex items-center gap-2 shrink-0 rounded-lg border border-[#E5E1DF] bg-[#F7F4F2]/40 px-2.5 py-1.5"
          >
            <div className={cn('rounded-md p-1', item.iconClass)}>
              <Icon className="h-3.5 w-3.5" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-semibold text-[#242424] tabular-nums">
                {item.value}
              </div>
              <div className="text-[10px] font-medium uppercase tracking-wide text-[#585755]">
                {item.label}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
